import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, RefreshCcw, Video, Trash2 } from "lucide-react";
import {
  getVideoReviewsAPI,
  approveVideoReviewAPI,
  rejectVideoReviewAPI,
  deleteVideoReviewAPI,
} from "../services/apicollection";
import VideoReviewForm from "../Components/VideoReviewForm";

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const VideoReviewManagementPage = () => {
  const controller = new AbortController();
  const [showForm, setShowForm] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getVideoReviewsAPI({ signal: controller.signal });
      if (res?.status && Array.isArray(res.data)) {
        setReviews(res.data);
      } else {
        setReviews([]);
        setError(res?.message || "Failed to load video reviews");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load video reviews");
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
    return () => {
      controller.abort();
    };
  }, [fetchReviews]);

  const handleSave = () => {
    setShowForm(false);
    fetchReviews();
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    const at = new Date(a?.createdAt || 0).getTime();
    const bt = new Date(b?.createdAt || 0).getTime();
    return bt - at;
  });

  const setApproval = async (review, approved) => {
    if (!review?._id) return;
    setUpdatingId(review._id);
    try {
      const res = approved
        ? await approveVideoReviewAPI(review._id)
        : await rejectVideoReviewAPI(review._id);

      if (!res?.status) {
        alert(res?.message || "Failed to update video review");
        return;
      }

      setReviews((prev) =>
        prev.map((r) =>
          r?._id === review._id ? { ...r, approved: !!approved } : r
        )
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this video review?"))
      return;
    try {
      const res = await deleteVideoReviewAPI(id);
      if (res?.status) {
        fetchReviews();
      } else {
        alert(res?.message || "Failed to delete");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete");
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <VideoReviewForm onSuccess={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">
          Video Reviews & Testimonials
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fetchReviews()}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0f1c2e] px-3 py-2 text-sm font-medium text-slate-200 ring-1 ring-slate-200 transition-colors hover:bg-[#0d1b2e]"
            title="Refresh"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
          >
            <Plus size={16} />
            Add Video Review
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
          {error}
        </div>
      ) : sortedReviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
          <Video size={36} className="text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No video reviews yet</p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
          >
            Add your first video review
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedReviews.map((r) => {
            const busy = updatingId === r?._id;
            const approved = !!r?.approved;
            return (
              <article
                key={r?._id}
                className="flex flex-col min-h-[520px] overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e] shadow-sm transition-shadow hover:shadow"
              >
                <div className="relative aspect-video h-[65%] w-full bg-[#13233a]">
                  <video
                    src={r?.videoUrl}
                    controls
                    className="h-full w-full object-cover"
                    preload="metadata"
                  />
                </div>
                <div className="flex min-h-0 flex-1 flex-col p-3">
                  <h4 className="truncate text-sm font-semibold text-slate-100 capitalize">
                    {r?.name || "—"}
                  </h4>
                  <p className="mt-0.5 text-xs text-slate-400 capitalize">
                    {r?.course || "—"}
                  </p>
                  {r?.message && (
                    <p className="mt-1  line-clamp-2 text-xs text-slate-400">
                      {r.message}
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        approved
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-rose-50 text-rose-700"
                      }`}
                    >
                      {approved ? "Approved" : "Rejected"}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {formatDateTime(r?.createdAt)}
                    </span>
                  </div>
                  <div className="mt-auto flex gap-2 pt-2">
                    <div className="inline-flex overflow-hidden rounded-lg border border-[#1d3350]">
                      <button
                        type="button"
                        onClick={() => setApproval(r, true)}
                        disabled={busy || approved}
                        className="px-2 py-1 text-xs font-medium hover:bg-emerald-50 disabled:opacity-60"
                      >
                        {busy && !approved ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          "Approve"
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setApproval(r, false)}
                        disabled={busy || !approved}
                        className="px-2 py-1 text-xs font-medium hover:bg-rose-50 disabled:opacity-60"
                      >
                        Reject
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(r._id)}
                      className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VideoReviewManagementPage;
